import { ErrorState } from "@/components/shared/error-state";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import type { BudgetAuditEntry, BudgetLineItem } from "@/hooks/use-budgets";
import { useBudgetAudit } from "@/hooks/use-budgets";

interface BudgetLineHistorySheetProps {
  projectId: string;
  lineItem: BudgetLineItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Side drawer rendering the audit trail for a single Buy Log line item.
 * Feeds from `GET /api/projects/:projectId/budget/audit?lineItemId=...`
 * which returns `auditLog` rows filtered to `entityType = "budget"`.
 */
export function BudgetLineHistorySheet({
  projectId,
  lineItem,
  open,
  onOpenChange,
}: BudgetLineHistorySheetProps) {
  const audit = useBudgetAudit(projectId, lineItem?.id ?? undefined);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Edit history</SheetTitle>
          <SheetDescription>
            {lineItem?.costCode?.code ? (
              <>
                <span className="font-medium">{lineItem.costCode.code}</span> —{" "}
                {lineItem.description}
              </>
            ) : (
              (lineItem?.description ?? "Budget line item")
            )}
          </SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto px-4 pb-4">
          {audit.isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : audit.isError ? (
            <ErrorState message={audit.error?.message} onRetry={() => audit.refetch()} />
          ) : (audit.data ?? []).length === 0 ? (
            <p className="text-sm text-gray-500">No history yet for this line.</p>
          ) : (
            <ol className="space-y-3">
              {(audit.data ?? []).map((entry) => (
                <AuditEntryCard key={entry.id} entry={entry} />
              ))}
            </ol>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function AuditEntryCard({ entry }: { entry: BudgetAuditEntry }) {
  const actionLabel =
    entry.action === "create"
      ? "Created"
      : entry.action === "update"
        ? "Updated"
        : entry.action === "delete"
          ? "Deleted"
          : entry.action;

  const stamp = new Date(entry.createdAt);
  const timeLabel = `${stamp.toLocaleDateString()} ${stamp.toLocaleTimeString()}`;

  return (
    <li className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
      <div className="flex items-center justify-between text-xs text-gray-500">
        <span className="font-semibold text-gray-700">{actionLabel}</span>
        <time dateTime={entry.createdAt}>{timeLabel}</time>
      </div>
      <div className="mt-1 text-xs text-gray-600">{entry.actorName ?? "Unknown user"}</div>
      {entry.changedFields && entry.changedFields.length > 0 ? (
        <dl className="mt-2 space-y-1 text-xs">
          {entry.changedFields.map((field) => (
            <div key={field} className="grid grid-cols-[6rem_1fr_1fr] gap-2">
              <dt className="font-medium text-gray-500">{field}</dt>
              <dd className="text-gray-400 line-through">
                {formatValue(entry.previousState?.[field])}
              </dd>
              <dd className="text-gray-800">{formatValue(entry.newState?.[field])}</dd>
            </div>
          ))}
        </dl>
      ) : null}
    </li>
  );
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}
